/**
 * Small deterministic PRNG (mulberry32) so the seeded dataset is stable
 * across reloads before it is persisted to localStorage.
 */
export function createRng(seed = 20260716) {
  let a = seed >>> 0
  const next = () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
  return {
    next,
    int: (min: number, max: number) => Math.floor(next() * (max - min + 1)) + min,
    pick: <T>(arr: readonly T[]): T => arr[Math.floor(next() * arr.length)],
    pickMany: <T>(arr: readonly T[], count: number): T[] => {
      const copy = [...arr]
      const out: T[] = []
      for (let i = 0; i < count && copy.length; i++) {
        out.push(copy.splice(Math.floor(next() * copy.length), 1)[0])
      }
      return out
    },
    bool: (p = 0.5) => next() < p,
    float: (min: number, max: number, dp = 2) =>
      Number((next() * (max - min) + min).toFixed(dp)),
  }
}

export type Rng = ReturnType<typeof createRng>

export const pad = (n: number, len = 3) => String(n).padStart(len, '0')
